import Controller from '@ember/controller';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type { IllustrationName } from '@mylighthouse/prism-ember/types/components/prism/illustration';
import { tKey } from 'ember-intl';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';

interface OnboardingStep {
  description: string;
  illustration: IllustrationName;
  linkIcon?: string;
  linkText: string;
  onClickLink: () => void;
}

export default class HotelBenchmarkInsightOnboardingController extends Controller {
  @service declare access: Services['access'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intercom: Services['intercom'];
  @service declare intl: Services['intl'];
  @service declare router: Services['router'];

  @tracked currentStep: number = 0;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get currentStepData() {
    return this.steps[this.currentStep];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get currentStepIndex() {
    return this.currentStep + 1;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isFirstStep() {
    return this.currentStep === 0;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isLastStep() {
    return this.currentStep === this.steps.length - 1;
  }

  get steps(): OnboardingStep[] {
    let firstStepDescriptionKey: string;

    if (this.access.hasBenchmarkInsightCompset) {
      firstStepDescriptionKey = tKey(
        'benchmark_insight.onboarding.pro.1.description',
      );
    } else if (this.access.hasBenchmarkInsightSmartCompset) {
      firstStepDescriptionKey = tKey(
        'benchmark_insight.onboarding.plus.1.description',
      );
    } else if (this.hotelAndMarketService.hotel.benchmarkInsightIsLight) {
      firstStepDescriptionKey = tKey(
        'benchmark_insight.onboarding.light.1.description',
      );
    } else {
      firstStepDescriptionKey = tKey(
        'benchmark_insight.onboarding.core.1.description',
      );
    }

    let lastStepDescriptionKey: string;

    if (this.access.hasBenchmarkInsightCompset) {
      lastStepDescriptionKey = tKey(
        'benchmark_insight.onboarding.pro.3.description',
      );
    } else {
      lastStepDescriptionKey = tKey(
        'benchmark_insight.onboarding.light.3.description',
      );
    }

    const steps = [
      {
        illustration: '200x200_hand' as const,
        description: this.intl.t(firstStepDescriptionKey, {
          htmlSafe: true,
          classNameBold: 'text-300--bold',
        }),
        linkText: this.intl.t('benchmark_insight.onboarding.light.link'),
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        onClickLink: () =>
          this.intercom.redirectToIntercomHelp(
            'en/articles/8272072-get-started-with-benchmark-insight',
          ),
        linkIcon: 'book',
      },
      {
        illustration: '200x200_ri-single-platform' as const,
        description: this.intl.t(
          'benchmark_insight.onboarding.light.2.description',
          {
            htmlSafe: true,
            classNameBold: 'text-300--bold',
          },
        ),
        linkText: this.intl.t('benchmark_insight.onboarding.light.link'),
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        onClickLink: () =>
          this.intercom.redirectToIntercomHelp(
            'en/articles/8272072-get-started-with-benchmark-insight',
          ),
        linkIcon: 'book',
      },
      {
        illustration: '200x200_benchmark-focus' as const,
        description: this.intl.t(lastStepDescriptionKey, {
          htmlSafe: true,
          classNameBold: 'text-300--bold',
        }),
        linkText: this.intl.t('benchmark_insight.onboarding.light.link'),
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        onClickLink: () =>
          this.intercom.redirectToIntercomHelp(
            'en/articles/8272072-get-started-with-benchmark-insight',
          ),
        linkIcon: 'book',
      },
    ];

    return steps;
  }

  @action onClickBack(): void {
    if (this.currentStep > 0) {
      this.currentStep -= 1;
    }
  }

  @action onClickNext(): void {
    if (this.currentStep < this.steps.length - 1) {
      this.currentStep += 1;
    }
  }

  @action onCloseModal(): void {
    // Use legacyTransitionTo to avoid the parent model reloading.
    // The data has already loaded in the background (can be seen through the modal overlay),
    // and it looks weird if the page reloads.
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.router.legacyTransitionTo('hotel.benchmark-insight.index');
  }
}
